import { useState, useMemo, useEffect, useCallback } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { useAuth, useCourses, useModal } from 'contexts';
import { Placeholder } from 'components';
import { getCourseStatus } from 'utils';
import Tree from './Tree';
import { ProgressInterface } from 'types';
import { Evaluate } from 'components/Evaluate';
import { FaStar, FaRegStar } from 'react-icons/fa6';
import { getEvaluate, getAverangeRating } from 'services';
import { Rating } from 'react-simple-star-rating';

import './style.scss';

import noImage from 'assets/images/no-image.jpg';

export default function CoursesDetailPage() {
  const { user } = useAuth();
  const { allProgress, listCourses } = useCourses();
  const { courseID } = useParams();
  const { showModal, closeModal } = useModal();

  const [evaluateContent, setEvaluateContent] = useState<boolean>(false);
  const [averageRating, setAverageRating] = useState<number>();

  const course = useMemo(() => {
    if (Array.isArray(listCourses) && listCourses.length) {
      return listCourses.find(x => x._id === courseID) || null;
    }
    return null;
  }, [courseID, listCourses]);

  const courseProgress = useMemo(() => {
    if (Array.isArray(allProgress) && allProgress.length) {
      return allProgress.find(x => x._id === courseID) || null;
    }
    return null;
  }, [allProgress, courseID]);

  const fetchAverageRating = useCallback(() => {
    if (courseID) {
      getEvaluate(user?._id, courseID).then(res => {
        if (res.length > 0) {
          setEvaluateContent(false);
        }
        getAverangeRating(courseID).then(res => {
          if (res.length > 0) {
            setAverageRating(res[0].averageRating);
          }
        });
      });
    }
  }, [user, courseID]);

  useEffect(() => {
    if (user && courseID && !evaluateContent) {
      getEvaluate(user._id, courseID).then(res => {
        if (res.length === 0) {
          setEvaluateContent(true);
        }
      });
      fetchAverageRating();
    }
  }, [user, courseID, evaluateContent, fetchAverageRating]);

  useEffect(() => {
    if (courseProgress && courseProgress?.percent === 100 && evaluateContent && courseProgress?._id !== 'certificate') {
      setTimeout(() => {
        showModal(
          'evaluate',
          <Evaluate contentId={course?._id} title={course?.title} type={course?.type} onClose={fetchAverageRating} />,
          'text-black evaluateContent',
        );
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseProgress, evaluateContent]);

  const handleEvaluateCourse = useCallback(() => {
    showModal(
      'evaluate',
      <Evaluate contentId={course?._id} title={course?.title} type={course?.type} onClose={fetchAverageRating} />,
      'text-black evaluateContent',
    );
  }, [course, showModal, fetchAverageRating]);

  if (!Array.isArray(listCourses)) {
    return (
      <div className="flex flex-col space-y-6">
        <div className="flex flex-row space-x-3">
          <Placeholder className="w-10 h-10 rounded-full" />
          <Placeholder className="flex-1 h-10 rounded-full" />
        </div>
        <Placeholder className="h-64" />
        <div className="grid grid-cols-3 h-48 gap-6">
          <Placeholder />
          <Placeholder />
          <Placeholder />
        </div>
      </div>
    );
  }

  if (!course) {
    return <Navigate to={`/erro-404`} replace />;
  }

  return (
    <section id="courses-detail">
      <div className="infos">
        <div className="img overflow-hidden mr-5 flex items-center justify-center">
          <img
            src={course.extra?.image?.length ? course.extra?.image : noImage}
            alt={course.title || ' '}
            className="w-full h-full block object-cover transition-transform duration-300 ease-in-out"
          />
        </div>
        <div className="data">
          {!!course.title && <h2>{course.title}</h2>}
          {!!getCourseStatus(courseProgress?.percent).length && (
            <p className="status text-sm lg:text-base not-italic font-bold leading-tight uppercase">
              {getCourseStatus(courseProgress?.percent)}
            </p>
          )}
          {typeof courseProgress?.percent === 'number' && courseProgress?.percent > 0 && (
            <div>
              <p className="progress text-sm lg:text-base text-black not-italic font-bold leading-tight">
                {`${Math.round(courseProgress?.percent)}% concluído`}
              </p>
              {courseProgress?._id !== 'certificate' && (
                <>
                  {evaluateContent && courseProgress?.percent === 100 ? (
                    <button
                      className="mt-5 flex items-center bg-[#503e9d40] text-[#503e9d] rounded-full py-1 px-3"
                      onClick={handleEvaluateCourse}
                    >
                      <FaStar className="mr-1" /> Avaliar
                    </button>
                  ) : (
                    <>
                      <p className="mt-4">Avaliação dos usuários</p>
                      <div className="flex items-center">
                        <Rating
                          initialValue={averageRating}
                          readonly={true}
                          emptyStyle={{ display: 'flex' }}
                          fillStyle={{ display: '-webkit-inline-box' }}
                          fillIcon={<FaStar size={15} />}
                          emptyIcon={<FaRegStar size={15} />}
                          titleSeparator={'de'}
                          allowFraction
                        />
                        <small className="ml-1 mt-1">{averageRating} de 5</small>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}
          {!!course.extra?.description && (
            <div className="description text-sm" dangerouslySetInnerHTML={{ __html: course.extra.description }} />
          )}
        </div>
      </div>
      {!!courseProgress && !!courseID && (
        <div className="content">
          <h3>Conteúdo programático</h3>
          <Tree items={courseProgress?.items as ProgressInterface[]} parent={courseProgress._id} courseID={courseID} />
        </div>
      )}
    </section>
  );
}
